/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import {
  EuiButton,
  EuiButtonEmpty,
  EuiForm,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiPanel,
  EuiSpacer,
  EuiText,
} from "@inscopix/ideas-eui";
import { useDataTableContext } from "pages/gdt/store/DataTableProvider";
import { useEffect, useState } from "react";
import { isDefined } from "utils/isDefined";
import { FieldTableComboBox } from "./fields/FieldTableComboBox";
import { gql } from "@apollo/client";
import { useModalMapAnalysisParametersLazyQuery } from "graphql/_Types";
import { ModalLoading } from "components/ModalLoading/ModalLoading";
import { ModalError } from "components/ModalError/ModalError";
import { FieldParamMapping } from "./fields/FieldParamMapping";
import assert from "assert";
import { isNonNullish } from "utils/isNonNullish";
import { Ast, AstNodeType } from "@inscopix/ideas-hyperformula";
import { addUtilityToastSuccess } from "utils/addUtilityToastSuccess";
import { addUtilityToastFailure } from "utils/addUtilityToastFailure";
import { ToolSpec } from "types/json-schemas/type_guards";

gql`
  query ModalMapAnalysisParameters($toolVersionId: Int!) {
    toolVersion: toolVersionById(id: $toolVersionId) {
      id
      toolSpec
    }
  }
`;

// Hook that fetches the tool spec used by the analysis table
const useToolSpec = (tableId: string) => {
  const toolVersionId = useDataTableContext((s) => {
    const table = s.tables.find(({ id }) => id === tableId);
    return table?.toolVersion?.id;
  });
  const [getToolSpec] = useModalMapAnalysisParametersLazyQuery();
  const [toolSpec, setToolSpec] = useState<ToolSpec>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error>();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        assert(toolVersionId !== undefined);
        const { data } = await getToolSpec({ variables: { toolVersionId } });
        const toolSpec = data?.toolVersion?.toolSpec;
        assert(toolSpec !== undefined);
        setToolSpec(toolSpec as unknown as ToolSpec);
      } catch (error) {
        setError(error as Error);
      } finally {
        setIsLoading(false);
      }
    };
    void fetchData();
  }, [getToolSpec, toolVersionId]);

  return { toolSpec, isLoading, error };
};

// Hook that calculates the initial tables selected as sources
export const useInitialSourceTables = (tableId: string) =>
  useDataTableContext((s) => {
    const table = s.tables.find(({ id }) => id === tableId);
    const sourcesColumnGroup = table?.columnGroups.find(
      (group) => group.definition?.kind === "sources",
    );

    if (!table || !sourcesColumnGroup) {
      return [];
    }

    return table.columns
      .map((column, idx) => {
        if (column.group !== sourcesColumnGroup.id) {
          return undefined;
        }

        const isDisabled = table.rows.some(
          (row) => row.cells[idx].formula !== "" && row.task !== null,
        );

        const tableId =
          column.definition.kind === "identifier"
            ? column.definition.table.id
            : undefined;

        if (tableId === undefined) {
          return undefined;
        }

        return { tableId, isDisabled };
      })
      .filter(isDefined);
  });

// Hook that calculates the initial analysis parameter mappings
const useInitialParamMap = (tableId: string) =>
  useDataTableContext((s) => {
    const table = s.tables.find(({ id }) => id === tableId);
    const paramMap: ParamMap = {};

    if (table === undefined) {
      return {};
    }

    table.columns.forEach((column) => {
      if (isNonNullish(column.default_formula) && isNonNullish(column.key)) {
        let ast: Ast;

        try {
          ast = s.formulaToAst({
            tableId: table.id,
            formula: column.default_formula,
          });
        } catch (error) {
          return;
        }

        if (
          ast.type === AstNodeType.FUNCTION_CALL &&
          ast.procedureName === "JOIN" &&
          ast.args[2].type === AstNodeType.COLUMN_RANGE &&
          ast.args[2].start.sheet !== undefined
        ) {
          const sheetId = ast.args[2].start.sheet;
          const columnIdx = ast.args[2].start.col;
          const sheetName = s.getSheetName({ sheetId });
          const sourceTable = s.tables.find((t) => t.key === sheetName);
          if (sourceTable && columnIdx < sourceTable.columns.length) {
            paramMap[column.key] = {
              tableId: sourceTable.id,
              columnId: sourceTable.columns[columnIdx].id,
            };
          }
        }
      }
    });

    return paramMap;
  });

type ParamMap = {
  [paramKey: string]:
    | {
        tableId: string | undefined;
        columnId: string | undefined;
      }
    | undefined;
};

interface ModalMapAnalysisParametersProps {
  onClose: () => void;
  tableId: string;
}

export const ModalMapAnalysisParameters = ({
  onClose,
  tableId,
}: ModalMapAnalysisParametersProps) => {
  const tables = useDataTableContext((s) => s.tables);
  const mapAnalysisParameters = useDataTableContext(
    (s) => s.mapAnalysisParameters,
  );
  const { toolSpec, isLoading, error } = useToolSpec(tableId);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const initialTables = useInitialSourceTables(tableId);
  const [tableIds, setTableIds] = useState(
    initialTables.map(({ tableId }) => tableId),
  );
  const initialParamMap = useInitialParamMap(tableId);
  const [paramMap, setParamMap] = useState(initialParamMap);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const { error } = await mapAnalysisParameters({
        tableId,
        sourceTableIds: tableIds,
        paramMap: Object.entries(paramMap)
          .filter(([_, mapping]) => mapping?.columnId !== undefined)
          .map(([param, mapping]) => {
            const table = tables.find(({ id }) => id === mapping?.tableId);
            assert(isDefined(table));
            return {
              param,
              column: {
                id: mapping?.columnId as string,
                kind:
                  table.kind === "data"
                    ? "data_table_column"
                    : "gdt_analysis_table_column",
              },
            };
          }),
      });
      assert(error === undefined);
      addUtilityToastSuccess("Analysis parameters mapped");
    } catch (error) {
      addUtilityToastFailure("Failed to map analysis parameters");
    }
    onClose();
  };

  if (isLoading) {
    return <ModalLoading title="Map Analysis Parameters" onClose={onClose} />;
  }

  if (error !== undefined) {
    return <ModalError title="Map Analysis Parameters" onClose={onClose} />;
  }

  return (
    <EuiModal
      onClose={onClose}
      css={css`
        width: 900px;
        min-width: 900px;
      `}
    >
      <EuiModalHeader>
        <EuiModalHeaderTitle component="h3">
          Map Analysis Parameters
        </EuiModalHeaderTitle>
      </EuiModalHeader>

      <EuiModalBody>
        <EuiText size="s">
          <p>
            Map columns from a source table to analysis parameters. This will
            allow you to auto-fill analysis parameters from selected source
            table rows.
          </p>
        </EuiText>

        <EuiSpacer />

        <EuiForm style={{ height: "100%", overflow: "hidden" }}>
          <EuiPanel color={"subdued"} hasBorder={false} hasShadow={false}>
            <FieldTableComboBox
              label="Source tables"
              disabledTableIds={initialTables
                .filter(({ isDisabled }) => isDisabled)
                .map(({ tableId }) => tableId)}
              includeTable={(table) => table.id !== tableId}
              value={tableIds}
              onChange={setTableIds}
            />
          </EuiPanel>
          <EuiSpacer />

          {toolSpec !== undefined && (
            <FieldParamMapping
              hideTableSelector={false}
              tableIdOptions={tableIds}
              inputsLabel="Linked analysis inputs"
              parametersLabel="Linked analysis parameters"
              toolSpec={toolSpec}
              value={paramMap}
              onChange={setParamMap}
            />
          )}
        </EuiForm>
      </EuiModalBody>

      <EuiModalFooter>
        <EuiButtonEmpty onClick={onClose}>Cancel</EuiButtonEmpty>
        <EuiButton
          isLoading={isSubmitting}
          fill
          onClick={() => void handleSubmit()}
        >
          Update
        </EuiButton>
      </EuiModalFooter>
    </EuiModal>
  );
};
